/**
 * useTajwidExercise — manage state for the per-surah Tajwīd exercise.
 *
 * Loads the list of "target" words (each carrying a single Tajwīd rule),
 * the inventory of rules and lessons (for distractor labels), and tracks
 * per-word answers + the running score. Score is persisted to
 * localStorage under the key `noor_albayan_tajwid_scores` keyed by surah.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api, { tajwidApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const SCORE_KEY = 'noor_albayan_tajwid_scores';

const loadScores = () => {
  try {
    return JSON.parse(localStorage.getItem(SCORE_KEY) || '{}');
  } catch {
    return {};
  }
};

const saveScores = (data) => {
  try {
    localStorage.setItem(SCORE_KEY, JSON.stringify(data));
  } catch { /* quota / disabled */ }
};

const wordKey = (ayahKey, wordIndex) => `${ayahKey}:${wordIndex}`;

export const useTajwidExercise = (surahNumber, enabled) => {
  const { isAuthenticated } = useAuth();
  const [targets, setTargets] = useState([]);              // [{ayahKey, wordIndex, rule}]
  const [rulesPresent, setRulesPresent] = useState([]);    // list of rule codes in this surah
  const [allRules, setAllRules] = useState([]);            // full RULE_INFO list (FR labels)
  const [answers, setAnswers] = useState({});              // {wordKey: {chosen, correct, rule}}
  const [loading, setLoading] = useState(false);

  // index target by word key for O(1) lookup
  const targetByKey = useMemo(() => {
    const m = new Map();
    for (const t of targets) m.set(wordKey(t.ayahKey, t.wordIndex), t);
    return m;
  }, [targets]);

  // Load rules + targets the first time exercise mode is enabled for a surah.
  const loadedFor = useRef(null);
  useEffect(() => {
    if (!enabled || !surahNumber) return;
    if (loadedFor.current === surahNumber) return;
    loadedFor.current = surahNumber;

    let cancelled = false;
    setLoading(true);
    Promise.all([
      tajwidApi.getRules().then((r) => r.data || []).catch(() => []),
      tajwidApi.getSurahExercise(surahNumber).then((r) => r.data || {}).catch(() => ({})),
    ]).then(([rules, ex]) => {
      if (cancelled) return;
      setAllRules(rules);
      setTargets(ex.targets || []);
      setRulesPresent(ex.rulesPresent || []);
      // Restore previous answers for this surah from localStorage
      const stored = loadScores();
      const surahData = stored[String(surahNumber)] || {};
      setAnswers(surahData.answers || {});
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [enabled, surahNumber]);

  // Reset cache when surah changes (so re-enable on a new surah re-fetches)
  useEffect(() => {
    loadedFor.current = null;
    setAnswers({});
    setTargets([]);
    setRulesPresent([]);
  }, [surahNumber]);

  /**
   * Build QCM choices for a target: correct rule + 3 distractors. The
   * distractors are picked first from rules ALSO present in this surah
   * (to keep the difficulty calibrated), then completed from the global
   * rule pool if needed.
   */
  const choicesFor = useCallback((target) => {
    if (!target) return [];
    const ruleByCode = new Map(allRules.map((r) => [r.rule, r]));
    const correct = ruleByCode.get(target.rule);
    if (!correct) return [];

    // Build distractor pool, preferring rules present in this surah.
    const localPool = rulesPresent.filter((r) => r !== target.rule);
    const globalPool = allRules
      .map((r) => r.rule)
      .filter((r) => r !== target.rule && !localPool.includes(r));
    const pool = [...localPool, ...globalPool];

    // Pick 3 distractors, deterministic per (ayahKey, wordIndex) so the
    // QCM doesn't reshuffle every render.
    const seed = (target.ayahKey + ':' + target.wordIndex)
      .split('')
      .reduce((acc, c) => (acc * 31 + c.charCodeAt(0)) >>> 0, 7);
    const shuffled = [...pool];
    let s = seed;
    for (let i = shuffled.length - 1; i > 0; i--) {
      s = (s * 1103515245 + 12345) >>> 0;
      const j = s % (i + 1);
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    const distractors = shuffled.slice(0, 3).map((code) => ruleByCode.get(code))
      .filter(Boolean);

    // Place the correct answer at a deterministic position.
    const all = [correct, ...distractors];
    s = (s * 1103515245 + 12345) >>> 0;
    const insertAt = s % all.length;
    if (insertAt !== 0) {
      const [c] = all.splice(0, 1);
      all.splice(insertAt, 0, c);
    }
    return all;
  }, [allRules, rulesPresent]);

  const answer = useCallback((target, chosenRule) => {
    if (!target) return null;
    const key = wordKey(target.ayahKey, target.wordIndex);
    if (answers[key]) return answers[key];        // already answered
    const correct = chosenRule === target.rule;
    const newAnswer = { chosen: chosenRule, correct, rule: target.rule };
    const next = { ...answers, [key]: newAnswer };
    setAnswers(next);

    // Persist locally
    const stored = loadScores();
    stored[String(surahNumber)] = {
      surahNumber,
      updatedAt: new Date().toISOString(),
      total: targets.length,
      answers: next,
    };
    saveScores(stored);

    // Persist as a granular attempt to power per-rule skill tracking
    // (best-effort, fire-and-forget — only for authenticated users).
    if (isAuthenticated) {
      api.post('/courses/tajweed-attempts', {
        surahNumber,
        ayahKey: target.ayahKey,
        wordIndex: target.wordIndex,
        expectedRuleId: target.rule,
        selectedRuleId: chosenRule,
      }).catch(() => { /* best-effort */ });
    }
    return newAnswer;
  }, [answers, surahNumber, targets.length, isAuthenticated]);

  const resetSurahScore = useCallback(() => {
    setAnswers({});
    const stored = loadScores();
    delete stored[String(surahNumber)];
    saveScores(stored);
  }, [surahNumber]);

  const stats = useMemo(() => {
    const answered = Object.keys(answers).length;
    const correct = Object.values(answers).filter((a) => a.correct).length;
    return {
      total: targets.length,
      answered,
      correct,
      percent: targets.length > 0 ? Math.round((correct / targets.length) * 100) : 0,
    };
  }, [answers, targets]);

  // Debounced sync to the backend so the user's score persists across
  // devices once they're signed in. Local state remains the source of
  // truth; the POST is fire-and-forget.
  useEffect(() => {
    if (!isAuthenticated || !enabled || !surahNumber) return;
    if (!targets.length || !Object.keys(answers).length) return;
    const tid = setTimeout(() => {
      api.post('/tajwid/scores', {
        surahNumber,
        correct: stats.correct,
        answered: stats.answered,
        total: stats.total,
        answers,
      }).catch(() => { /* best-effort */ });
    }, 1500);
    return () => clearTimeout(tid);
  }, [isAuthenticated, enabled, surahNumber, answers, targets.length,
      stats.correct, stats.answered, stats.total]);

  const targetFor = useCallback(
    (ayahKey, wordIndex) => targetByKey.get(wordKey(ayahKey, wordIndex)) || null,
    [targetByKey],
  );

  const answerFor = useCallback(
    (ayahKey, wordIndex) => answers[wordKey(ayahKey, wordIndex)] || null,
    [answers],
  );

  return {
    enabled,
    loading,
    targets,
    rulesPresent,
    allRules,
    targetFor,
    answerFor,
    choicesFor,
    answer,
    resetSurahScore,
    stats,
  };
};
